"use client"

/**
 * /blogs/news — editorial homepage client (Phase 2b migration + Phase B
 * refresh).
 *
 * Every paginated page renders the same magazine layout: latest-news grid +
 * sidebar (picks + Free-tool callout), full-width BrandLogoGrid in the article
 * column, Browse by Category sub-grids, two product ScrollCarousels (Top rated,
 * Editor's picks of the season), and the conditional Recently Viewed rail at
 * the bottom.
 *
 * Two elements are page-1-only:
 *   - The featured-story hero (daily-rotation pick — front-page showcase).
 *   - The "Trending right now" sidebar list (slots 13-16 of the curated
 *     22-article editorial-hero feed).
 *
 * On page 2+ the news grid swaps from the curated feed to the standard
 * paginated `initialData.articles` so users navigate the full backlog.
 */

import { RETAILER_INTEGRATIONS_DISPLAY } from '@/lib/site-stats'
import * as React from 'react'
import { formatPrice } from '@/lib/format-utils'
import Link from 'next/link'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { Suspense } from 'react'
import { ScrollCarousel } from '@/components/brand-kit/scroll-carousel'
import { Breadcrumb } from '@/components/brand-kit/breadcrumb'
import { blogBreadcrumbs } from '@/lib/breadcrumbs'
import { ProductCarouselCard } from '@/components/brand-kit/product-carousel-card'
import { ArticleCard } from '@/components/brand-kit/article-card'
import { CategoryCard } from '@/components/brand-kit/category-card'
import { BrandLogoGrid } from '@/components/brand-kit/brand-logo-grid'
import { SidebarPick } from '@/components/brand-kit/sidebar-pick'
import { RecentlyViewedCarousel } from '@/components/brand-kit/recently-viewed-carousel'
import { DarkCallout } from '@/components/brand-kit/dark-callout'
import { FeaturedStoryCard } from '@/components/brand-kit/featured-story-card'
import { TrendingList } from '@/components/brand-kit/trending-list'
import { ToolShareBar } from '@/components/brand-kit/tool-share-bar'
import type { CarouselProduct } from '@/components/brand-kit/product-carousel-card'
import type { RecommendedProduct } from '@/lib/recommendations'
import { buildExcerpt } from '@/lib/blog-excerpt'
import type { BlogsNewsArticle, BrandLogo } from '@/lib/blog-data'
import { BLOG_TAGS } from '@/lib/blog-tags'

interface RawArticle {
  id: number
  slug: string
  title: string
  content: string
  published_at: string | null
  updated_at: string
  author_name: string | null
  /** Resolved from the `authors` table server-side; null for no person author. */
  authorImageUrl?: string | null
  image: string | null
  imageAlt: string
  raw: any
}

interface NewsPageClientProps {
  initialData: {
    articles: RawArticle[]
    totalArticles: number
    totalPages: number
    currentPage: number
  }
  editorial: {
    /** Article shape sources differ between page 1 and page 2+:
     *   - page 1 populates `articles` (the editorial-hero curated 22-article
     *     feed) and `featured` (today's rotation pick)
     *   - page 2+ leaves both empty; the magazine grid falls back to the
     *     paginated `initialData.articles` and the featured hero + trending
     *     sidebar list are skipped. Everything else (sidebar editor's picks,
     *     brand grid, carousels, recently-viewed) renders identically.
     */
    articles: BlogsNewsArticle[]
    featured: BlogsNewsArticle | null
    featuredAll: BlogsNewsArticle[]
    brands: BrandLogo[]
    picks: RecommendedProduct[]
    topRated: RecommendedProduct[]
    seasonPicks: RecommendedProduct[]
    recentSeed: RecommendedProduct[]
  }
}

// ---------------------------------------------------------------------------
// Browse by Category sub-grids — mirrors the existing live page taxonomy
// (Type / Guides / Features / Specialty), brand directory now lives in the
// BrandLogoGrid section above this.
// ---------------------------------------------------------------------------

interface CatEntry {
  label: string
  href: string
  imageUrl?: string
  icon?: string
  meta?: string
}

const SHOP_BY_TYPE: CatEntry[] = [
  { label: 'French Door', href: '/french-door-refrigerators', imageUrl: 'https://images.unsplash.com/photo-1588854337115-1c67d9247e4d?w=600&q=80&auto=format&fit=crop', meta: 'Wide shelving' },
  { label: 'Side-by-Side', href: '/side-by-side-refrigerators', imageUrl: 'https://images.unsplash.com/photo-1721613877687-c9099b698faa?w=600&q=80&auto=format&fit=crop', meta: 'Narrow swing' },
  { label: 'Top Freezer', href: '/top-freezer-refrigerators', imageUrl: 'https://images.unsplash.com/photo-1630459065645-549fe5a56db4?w=600&q=80&auto=format&fit=crop', meta: 'Classic value' },
  { label: 'Bottom Freezer', href: '/bottom-freezer-refrigerators', imageUrl: 'https://images.thdstatic.com/productImages/bdc9483b-8578-469f-b68c-1f40eae38bf9/svn/monochromatic-stainless-steel-amana-bottom-freezer-refrigerators-abb2224brm-64_600.jpg', meta: 'Eye-level' },
  { label: 'Counter Depth', href: '/counter-depth-refrigerators', imageUrl: 'https://images.unsplash.com/photo-1722859178634-ccc8ea5680d2?w=600&q=80&auto=format&fit=crop', meta: 'Sleek fit' },
  { label: 'Mini Fridges', href: '/mini-fridges', imageUrl: 'https://images.unsplash.com/photo-1540961403310-79825242906e?w=600&q=80&auto=format&fit=crop', meta: 'Compact' },
  { label: 'Built-In', href: '/built-in-refrigerators', imageUrl: 'https://images.unsplash.com/photo-1610733374054-59454fe657cd?w=600&q=80&auto=format&fit=crop', meta: 'Custom fit' },
  { label: 'Smart Fridges', href: '/smart-fridges', imageUrl: 'https://images.unsplash.com/photo-1670272506253-3a185e06d102?w=600&q=80&auto=format&fit=crop', meta: 'Connected' },
  { label: 'Wine Coolers', href: '/collections/wine-fridges', imageUrl: 'https://images.unsplash.com/photo-1578911373434-0cb395d2cbfb?w=600&q=80&auto=format&fit=crop', meta: 'Cellar temp' },
  { label: 'Garage', href: '/garage-refrigerators', imageUrl: 'https://images.unsplash.com/photo-1635108198854-26645ffe6714?w=600&q=80&auto=format&fit=crop', meta: 'Extreme temps' },
]

const GUIDES: CatEntry[] = [
  { label: 'Refrigerator Buying Guide', href: '/refrigerator-buying-guide', icon: '📘', meta: 'How to choose' },
  { label: 'Freezer Buying Guide', href: '/freezer-buying-guide', icon: '📗', meta: 'Chest vs upright' },
  { label: 'Energy Efficiency', href: '/energy-efficiency-guide', icon: '⚡', meta: 'Save electricity' },
  { label: 'Sizing Guide', href: '/refrigerator-sizing-guide', icon: '📏', meta: 'Right size' },
  { label: 'Comparisons', href: '/refrigerator-comparisons', icon: '⚖️', meta: 'Side-by-side' },
]

const FEATURES: CatEntry[] = [
  { label: 'ENERGY STAR', href: '/energy-efficient-refrigerators', icon: '⭐', meta: 'Save on bills' },
  { label: 'Large Capacity', href: '/large-capacity-refrigerators', icon: '📦', meta: '25+ cu. ft.' },
  { label: 'Luxury', href: '/luxury-refrigerators', icon: '💎', meta: 'Premium brands' },
  { label: 'Apartment Size', href: '/apartment-size-refrigerators', icon: '🏢', meta: 'Space savers' },
  { label: 'Quiet', href: '/quiet-refrigerators', icon: '🔇', meta: 'Under 40 dB' },
]

const SPECIALTY: CatEntry[] = [
  { label: 'Wine & Beverage', href: '/wine-beverage-refrigerators', icon: '🍷', meta: 'Perfect temp' },
  { label: 'Ice Makers', href: '/ice-makers', icon: '🧊', meta: 'Nugget & clear' },
  { label: 'Kegerators', href: '/kegerators', icon: '🍺', meta: 'Draft beer' },
  { label: 'Outdoor', href: '/outdoor-refrigerators', icon: '🌞', meta: 'Weatherproof' },
  { label: 'RV', href: '/rv-refrigerators', icon: '🚐', meta: 'Road ready' },
  { label: 'Commercial', href: '/commercial-refrigerators', icon: '🏪', meta: 'Restaurant use' },
]

function CategoryGroup({
  eyebrow,
  title,
  items,
  cols,
}: {
  eyebrow: string
  title: string
  items: CatEntry[]
  cols: number
}) {
  return (
    <div style={{ marginBottom: 36 }}>
      <div className="ed-eyebrow" style={{ marginBottom: 8 }}>
        <span className="dot" />
        {eyebrow}
      </div>
      <h3
        style={{
          fontSize: 22,
          fontWeight: 800,
          letterSpacing: '-0.02em',
          color: 'var(--gray-900)',
          margin: '0 0 16px',
        }}
      >
        {title}
      </h3>
      <div
        className={`grid grid-cols-2 sm:grid-cols-3 ${
          cols === 6 ? 'lg:grid-cols-6' : cols === 5 ? 'lg:grid-cols-5' : cols === 4 ? 'lg:grid-cols-4' : 'lg:grid-cols-3'
        }`}
        style={{ gap: 14 }}
      >
        {items.map((cat) => (
          <CategoryCard
            key={cat.href}
            label={cat.label}
            href={cat.href}
            imageUrl={cat.imageUrl}
            icon={cat.icon}
            meta={cat.meta}
          />
        ))}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Convert RecommendedProduct → CarouselProduct for ScrollCarousel cards
// ---------------------------------------------------------------------------

function toCarouselProduct(p: RecommendedProduct, badge: string, badgeColor: string): CarouselProduct {
  // Canonical display rule (owner ruling 07/25/2026): whole dollars drop
  // ".00", real cents render exactly. Shared implementation in lib/format-utils.
  const formatUSD = (n?: number) =>
    typeof n === 'number' && n > 0 ? formatPrice(n) : ''
  return {
    brand: p.brand,
    title: p.title,
    price: formatUSD(p.price),
    originalPrice: formatUSD(p.originalPrice),
    badge,
    badgeColor,
    image: (p.images && p.images[0]) || '',
    images: p.images ?? [],
    href: p.canonical_url || `/products/${p.id}`,
    rating: p.rating,
    reviews: p.reviews,
  }
}

// ---------------------------------------------------------------------------
// Main client
// ---------------------------------------------------------------------------

export default function NewsPageClient({ initialData, editorial }: NewsPageClientProps) {
  const { totalArticles, totalPages, currentPage } = initialData
  const isFirstPage = currentPage === 1

  // FRIDGE-495: pagination links target #latest-news (pages 2+ repeat the
  // page-1 masthead per the 08/06 ruling, so landing at the top of an
  // identical hero reads as "the click did nothing"). The links pass
  // scroll={false} — Next's own scroll-to-top otherwise overrides any hash
  // landing — and this effect owns the scroll. One assert is not enough: the
  // page streams, and content above the grid finishing its layout pushes the
  // section back down after an early scrollIntoView. So the scroll re-asserts
  // over the settle window and stops the moment the visitor scrolls on their
  // own.
  React.useEffect(() => {
    if (window.location.hash !== '#latest-news') return
    let cancelled = false
    const cancel = () => { cancelled = true }
    const opts = { passive: true } as AddEventListenerOptions
    window.addEventListener('wheel', cancel, opts)
    window.addEventListener('touchstart', cancel, opts)
    window.addEventListener('keydown', cancel)
    const land = () => {
      if (!cancelled) document.getElementById('latest-news')?.scrollIntoView()
    }
    land()
    const timers = [200, 600, 1200].map((ms) => window.setTimeout(land, ms))
    return () => {
      timers.forEach(clearTimeout)
      window.removeEventListener('wheel', cancel)
      window.removeEventListener('touchstart', cancel)
      window.removeEventListener('keydown', cancel)
    }
  }, [currentPage])

  // Owner ruling 08/06/2026: EVERY listing page renders the same template —
  // one featured hero + six posts. The rotation hero therefore renders on
  // every page (it was page-1-only); the trending list stays page-1-only.
  const featured = editorial.featured
  const featuredSlug = featured?.slug

  // News-grid source per page:
  //   page 1 -> editorial.articles (the day's randomized page-1 feed)
  //   page 2+ -> initialData.articles (the standard 6-per-page paginated set)
  // The page-2+ articles arrive in `RawArticle` shape; map them to the same
  // `BlogsNewsArticle` shape the grid expects.
  const archiveAsBlogsNews: BlogsNewsArticle[] = initialData.articles.map((a) => {
    const text = (a.content || '').replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim()
    // Same legacy-header strip the server-side formatter applies, so page 2+
    // cards never show the title twice either (owner-reported 08/06/2026).
    const excerpt = buildExcerpt(a.content, a.title, 140)
    return {
      id: a.id,
      slug: a.slug,
      title: a.title,
      excerpt,
      image: a.image || '',
      imageAlt: a.imageAlt || `Image for ${a.title}`,
      publishedAt: a.published_at,
      publishedAtIso: a.published_at,
      authorName: a.author_name,
      authorImageUrl: a.authorImageUrl ?? null,
      // Latest update when present, else publication — one date, no prefix.
      latestDateIso: a.updated_at || a.published_at,
      readTimeMinutes: Math.max(1, Math.round(text.split(/\s+/).filter(Boolean).length / 220)),
      href: `/blogs/news/${a.slug}`,
      badge: 'News',
    }
  })

  const sourceArticles = isFirstPage ? editorial.articles : archiveAsBlogsNews
  // ONE hero + SIX posts, on every page (owner ruling 08/06/2026 — was 12).
  const latest = sourceArticles
    .filter((a) => a.slug !== featuredSlug)
    .slice(0, 6)
  // Trending sidebar list is only meaningful when we have the curated page-1
  // feed (positions 7-10). On page 2+ the source is the paginated 6-feed which
  // doesn't have enough articles to fill it AND wouldn't represent "trending"
  // anyway — so skip it on those pages.
  const trending = isFirstPage
    ? editorial.articles.filter((a) => a.slug !== featuredSlug).slice(6, 10)
    : []

  // Dynamic sidebar pick count — match the article column height
  const articleRows = Math.ceil(latest.length / 3)
  const articleColumnHeight =
    50 +
    articleRows * 290 +
    Math.max(0, articleRows - 1) * 20 +
    80 +
    (editorial.brands.length > 0 ? 700 : 0)
  const sidebarFixedHeight = 250 + 280
  const pickHeight = 400
  const gapBetweenItems = 18
  const availableForPicks = articleColumnHeight - sidebarFixedHeight - 5 * gapBetweenItems
  const sidebarPicksTarget = Math.max(2, Math.min(8, Math.floor(availableForPicks / pickHeight)))
  const sidebarTopCount = Math.min(3, sidebarPicksTarget)
  const sidebarBottomCount = Math.max(0, sidebarPicksTarget - sidebarTopCount)

  return (
    <div className="ed-root" style={{ background: '#fff' }}>
      {/* Hero */}
      <div className="ed-container" style={{ paddingTop: 32, paddingBottom: 28 }}>
        {/* ONE trail (lib/breadcrumbs.ts) — news-render.tsx emits the same
            blogBreadcrumbs({ page }) as the BreadcrumbList (FRIDGE-269). */}
        <Breadcrumb variant="editorial" trail={blogBreadcrumbs({ page: currentPage })} style={{ marginBottom: 24 }} />
        <div className="ed-eyebrow" style={{ marginBottom: 12 }}>
          <span className="dot" />
          Editorial · {totalArticles.toLocaleString()} articles
        </div>
        <h1
          className="ed-h1"
          style={{ fontSize: 'clamp(32px, 4vw, 52px)', marginBottom: 16, maxWidth: 820 }}
        >
          Fridge.com Blog — <em>fridges, freezers,</em> and kitchen stuff.
        </h1>
        <p className="ed-subtitle" style={{ maxWidth: 720, margin: '0 0 22px' }}>
          Independent reviews, head-to-head comparisons, and how-tos from people who actually live with these things.
        </p>
        <div style={{ marginBottom: 36 }}>
          <ToolShareBar title="Fridge.com Blog" />
        </div>
      </div>

      {/* Featured story — EVERY page (owner ruling 08/06/2026: each listing
          page is exactly one hero + six posts). */}
      {featured && (
        <FeaturedStoryCard
          article={featured}
          // FRIDGE-1504 L1504-9: this section sits directly under the h1.
          headingLevel={2}
          rotationMeta={
            editorial.featuredAll.length > 1
              ? `1 of ${editorial.featuredAll.length} · rotates daily`
              : null
          }
        />
      )}

      {/* Latest news + sidebar. Top padding adapts to whether a featured-
         story hero sits above (page 1 only): with the hero present, 48px
         feels natural; on archive pages the hero is missing so the same
         48px on top of the hero's own paddingBottom:28 added ~76px of
         empty space above "Latest news". Drop to 12px on those pages and
         let the hero's bottom padding carry the gap. */}
      <section id="latest-news" style={{ scrollMarginTop: 90 }}>
        <div
          className="ed-container"
          style={{ paddingTop: featured ? 48 : 12, paddingBottom: 48 }}
        >
          <div className="ed-grid-blogs-news">
            <div>
              <div className="ed-section-rule" style={{ marginBottom: 24 }}>
                <h2 style={{ fontSize: 26 }}>Latest news</h2>
                <span className="meta">Updated weekly</span>
              </div>
              <div className="ed-grid-news-cards">
                {latest.map((a) => (
                  <ArticleCard
                    key={a.id}
                    href={a.href}
                    imageUrl={a.image}
                    badge={a.badge}
                    title={a.title}
                    author={a.authorName || undefined}
                    authorImageUrl={a.authorImageUrl}
                    date={a.latestDateIso || ''}
                    excerpt={a.excerpt}
                  />
                ))}
              </div>

              {/* Pagination — wired to ?page=N URL params */}
              <Pagination currentPage={currentPage} totalPages={totalPages} />

              {/* Browse by topic — internal links to every tag page. Before this,
                 tag pages were in the sitemap but had ZERO internal links (orphaned
                 from the link graph); this gives each one a link from the blog index. */}
              {isFirstPage && (
                <nav aria-label="Browse articles by topic" className="mt-12 border-t border-gray-200 pt-8">
                  <h2 className="text-sm font-black uppercase tracking-wide text-gray-500 mb-4">Browse by topic</h2>
                  <ul className="flex flex-wrap gap-2">
                    {Object.values(BLOG_TAGS).map(tag => (
                      <li key={tag.slug}>
                        <Link
                          href={`/blogs/news/tags/${tag.slug}`}
                          className="inline-block rounded-full border border-gray-200 px-3 py-1.5 text-[13px] text-gray-700 transition-colors hover:border-[#ff9951] hover:text-[#ff9951]"
                        >
                          {tag.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </nav>
              )}

              {isFirstPage && editorial.brands.length > 0 && (
                <BrandLogoGrid
                  brands={editorial.brands}
                  title="Shop by manufacturer"
                  description={`Premium and mainstream refrigerator brands at Fridge.com — every model, every spec, real prices from ${RETAILER_INTEGRATIONS_DISPLAY} retailers and manufacturer stores.`}
                  initialCount={20}
                  expandStep={10}
                  searchable
                  searchPlaceholder={`Search ${editorial.brands.length.toLocaleString()} brands...`}
                />
              )}
            </div>

            <aside className="ed-sidebar-aside">
              {(editorial.picks || []).slice(0, sidebarTopCount).map((p) => (
                <SidebarPick key={p.id} product={p} />
              ))}

              {/* Trending list — page 1 only (uses the curated 22-feed positions
                  13-16). On archive pages we skip it; the sidebar still shows
                  editor's picks + dark callouts on every page. */}
              {trending.length > 0 && (
                <TrendingList
                  items={trending}
                />
              )}

              <DarkCallout
                variant="dark"
                eyebrow="Free tool"
                headline={
                  <>
                    What does <em style={{ color: 'var(--fridge-orange)', fontStyle: 'italic' }}>your</em> fridge cost to run?
                  </>
                }
                body="Enter model + ZIP. We pull DOE kWh and your local utility rate."
                href="/tools/refrigerator-electricity-cost-estimator"
                cta="Open calculator ↗"
              />

              {(editorial.picks || []).slice(sidebarTopCount, sidebarTopCount + sidebarBottomCount).map((p) => (
                <SidebarPick key={p.id} product={p} />
              ))}
            </aside>
          </div>
        </div>
      </section>

      {/* Browse by category — page-1-only chrome. On paginated archive pages
          this static hub grid is duplicate boilerplate, so it is dropped to
          keep page 2+ slim [A-32]. */}
      {isFirstPage && (
        <section style={{ background: 'var(--gray-50)', borderTop: '1px solid var(--gray-200)' }}>
          <div className="ed-container" style={{ paddingTop: 56, paddingBottom: 24 }}>
            <div className="ed-section-rule" style={{ marginBottom: 32 }}>
              <h2 style={{ fontSize: 26 }}>Browse by category</h2>
              <span className="meta">All 46 hubs</span>
            </div>
            <CategoryGroup eyebrow="Shop by type" title="Refrigerator types" items={SHOP_BY_TYPE} cols={5} />
            <CategoryGroup eyebrow="Guides & comparisons" title="Research & buying guides" items={GUIDES} cols={5} />
            <CategoryGroup eyebrow="By feature & need" title="Find by what matters" items={FEATURES} cols={5} />
            <CategoryGroup eyebrow="Specialty & commercial" title="Niche & professional" items={SPECIALTY} cols={6} />
          </div>
        </section>
      )}

      {/* Product carousels — page-1-only chrome [A-32] */}
      {isFirstPage && editorial.topRated.length > 0 && (
        <ScrollCarousel
          eyebrow="Top rated"
          title="The highest-rated refrigerators on Fridge.com"
          bgClass="bg-white"
        >
          {editorial.topRated.map((p) => (
            <ProductCarouselCard
              key={p.id}
              product={toCarouselProduct(p, 'Top rated', 'bg-[#ff9951]')}
            />
          ))}
        </ScrollCarousel>
      )}

      {isFirstPage && editorial.seasonPicks.length > 0 && (
        <ScrollCarousel
          eyebrow="Editor's picks of the season"
          title="Premium picks our editors keep coming back to"
          bgClass="bg-white"
        >
          {editorial.seasonPicks.map((p) => (
            <ProductCarouselCard
              key={p.id}
              product={toCarouselProduct(p, "Editor's pick", 'bg-[#0469f0]')}
            />
          ))}
        </ScrollCarousel>
      )}

      {/* Recently-viewed rail — page-1-only chrome [A-32] */}
      {isFirstPage && (
        <Suspense fallback={null}>
          <RecentlyViewedCarousel
            pool={editorial.recentSeed.map((p) => ({
              id: p.id,
              product: toCarouselProduct(p, 'Recently viewed', 'bg-[#374151]'),
            }))}
          />
        </Suspense>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Pagination — preserves the live URL pattern (?page=N), with mobile collapse
// ---------------------------------------------------------------------------

function Pagination({ currentPage, totalPages }: { currentPage: number; totalPages: number }) {
  // Every pagination target carries #latest-news (FRIDGE-495): pages 2+ render
  // the exact page-1 template above the grid (owner ruling 08/06/2026), so a
  // click that lands at the top of an IDENTICAL masthead reads as "pagination
  // is broken". The hash lands the visitor on the content that changed. The
  // rel attributes are the real prev/next signals — the old metadata `other:`
  // entries rendered as <meta name="rel-prev">, which no engine reads.
  // FRIDGE-1504 L1504-12: below 640 px the words "Previous" and "Next" are
  // visually hidden (max-sm:sr-only), not display:none, so each control keeps
  // its name at every width; the name is the visible word, never an aria-label.
  const HASH = '#latest-news'
  const pageHref = (p: number) => (p <= 1 ? `/blogs/news${HASH}` : `/blogs/news?page=${p}${HASH}`)
  const prevHref = currentPage > 1 ? pageHref(currentPage - 1) : null
  const nextHref = currentPage < totalPages ? pageHref(currentPage + 1) : null
  const isPrevDisabled = currentPage === 1
  const isNextDisabled = currentPage >= totalPages

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexWrap: 'wrap',
        gap: 8,
        marginTop: 36,
        paddingTop: 28,
        borderTop: '1px solid var(--gray-100)',
      }}
    >
      {prevHref ? (
        <Link
          href={prevHref}
          rel="prev"
          scroll={false}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '9px 14px',
            border: '1px solid var(--gray-200)',
            borderRadius: 8,
            background: '#fff',
            fontSize: 13,
            fontWeight: 700,
            color: 'var(--gray-900)',
            textDecoration: 'none',
          }}
        >
          <ChevronLeft className="w-4 h-4" />
          <span className="max-sm:sr-only">Previous</span>
        </Link>
      ) : (
        // A disabled control is not a link — <a href="#"> put a dead anchor in
        // the crawlable markup on every page 1.
        <span
          aria-disabled="true"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '9px 14px',
            border: '1px solid var(--gray-200)',
            borderRadius: 8,
            background: '#fff',
            fontSize: 13,
            fontWeight: 700,
            color: 'var(--gray-400)',
          }}
        >
          <ChevronLeft className="w-4 h-4" />
          <span className="max-sm:sr-only">Previous</span>
        </span>
      )}
      <span
        className="sm:hidden"
        style={{ fontSize: 13, fontWeight: 700, color: 'var(--gray-700)', padding: '0 8px' }}
      >
        {currentPage} / {totalPages.toLocaleString()}
      </span>
      <div className="hidden sm:flex items-center gap-2">
        {currentPage > 2 && (
          <Link
            href={pageHref(1)}
            scroll={false}
            style={pageButtonStyle(false)}
          >
            1
          </Link>
        )}
        {currentPage > 3 && <span style={{ color: 'var(--gray-400)', padding: '0 4px' }}>…</span>}
        {currentPage > 1 && (
          <Link
            href={pageHref(currentPage - 1)}
            rel="prev"
            scroll={false}
            style={pageButtonStyle(false)}
          >
            {currentPage - 1}
          </Link>
        )}
        <span style={pageButtonStyle(true)}>{currentPage}</span>
        {currentPage < totalPages && (
          <Link
            href={pageHref(currentPage + 1)}
            rel="next"
            scroll={false}
            style={pageButtonStyle(false)}
          >
            {currentPage + 1}
          </Link>
        )}
        {currentPage < totalPages - 1 && (
          <Link
            href={pageHref(currentPage + 2)}
            scroll={false}
            style={pageButtonStyle(false)}
          >
            {currentPage + 2}
          </Link>
        )}
        {currentPage < totalPages - 2 && <span style={{ color: 'var(--gray-400)', padding: '0 4px' }}>…</span>}
        {/* < totalPages - 2, not - 1: at currentPage = totalPages - 2 the +2
            link above IS the last page, and the old bound rendered a second,
            duplicate last-page button beside it (FRIDGE-495). */}
        {currentPage < totalPages - 2 && (
          <Link
            href={pageHref(totalPages)}
            scroll={false}
            style={pageButtonStyle(false)}
          >
            {totalPages.toLocaleString()}
          </Link>
        )}
      </div>
      {nextHref ? (
        <Link
          href={nextHref}
          rel="next"
          scroll={false}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '9px 14px',
            border: '1px solid var(--gray-200)',
            borderRadius: 8,
            background: '#fff',
            fontSize: 13,
            fontWeight: 700,
            color: 'var(--gray-900)',
            textDecoration: 'none',
          }}
        >
          <span className="max-sm:sr-only">Next</span>
          <ChevronRight className="w-4 h-4" />
        </Link>
      ) : (
        <span
          aria-disabled="true"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '9px 14px',
            border: '1px solid var(--gray-200)',
            borderRadius: 8,
            background: '#fff',
            fontSize: 13,
            fontWeight: 700,
            color: 'var(--gray-400)',
          }}
        >
          <span className="max-sm:sr-only">Next</span>
          <ChevronRight className="w-4 h-4" />
        </span>
      )}
    </div>
  )
}

const pageButtonStyle = (active: boolean): React.CSSProperties => ({
  width: active ? 40 : 'auto',
  minWidth: 40,
  height: 40,
  padding: '0 10px',
  borderRadius: 8,
  border: active ? '2px solid var(--fridge-blue)' : '1px solid var(--gray-200)',
  background: active ? 'rgba(4,105,240,0.06)' : '#fff',
  fontSize: 13,
  fontWeight: active ? 800 : 600,
  color: active ? 'var(--fridge-blue)' : 'var(--gray-700)',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  textDecoration: 'none',
})
